import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download } from 'lucide-react';

export default function PWAInstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Detect iOS
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Check if user dismissed recently (don't show again for 7 days)
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedAt < sevenDays) return;
    }

    // Listen for the beforeinstallprompt event (Chrome/Edge/Android)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // For iOS, show the manual instruction prompt after a short delay
    if (ios && !standalone) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handler);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't show if already in standalone or nothing to show
  if (isStandalone || !showPrompt) return null;

  return (
    <div 
      data-testid="pwa-install-prompt"
      className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-50 animate-in slide-in-from-bottom-4 duration-500"
    >
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl shadow-2xl p-4 backdrop-blur-xl">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-[#162d50] flex items-center justify-center">
            <Download className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[var(--text)]">
              {t('pwa.title', 'CarmaCircle installieren')}
            </p>
            <p className="text-xs text-[var(--muted)] mt-0.5 leading-relaxed">
              {isIOS 
                ? t('pwa.iosHint', 'Tippe auf "Teilen" und dann "Zum Home-Bildschirm".')
                : t('pwa.description', 'Nutze CarmaCircle wie eine App auf deinem Geraet.')
              }
            </p>
          </div>
          <button
            data-testid="pwa-install-dismiss"
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Schliessen"
          >
            <X className="w-4 h-4 text-[var(--muted)]" />
          </button>
        </div>
        
        {!isIOS && deferredPrompt && (
          <button
            data-testid="pwa-install-button"
            onClick={handleInstall}
            className="mt-3 w-full text-sm font-medium py-2.5 rounded-xl bg-[#162d50] text-white hover:bg-[#1d3a66] active:scale-[0.98] transition-all"
          >
            {t('pwa.installButton', 'Jetzt installieren')}
          </button>
        )}
      </div>
    </div>
  );
}
